import {useEffect} from 'react'
import {useRouter} from 'next/router'
import {logMessage} from '@/services/logger/client'

declare global {
	interface Window {
		mikMakLoaded?: boolean
	}
}

interface MikMakCoordinatorProps {
	mikMakAppId: string | null
}

/**
 * Coordinator component that loads MikMak SDK only when needed.
 * Detects the presence of MikMak blocks on the page (buttons or widgets)
 * and loads the MikMak SDK script.
 */
export function MikMakCoordinator({mikMakAppId}: MikMakCoordinatorProps): null {
	const router = useRouter()

	useEffect(() => {
		const win = globalThis as typeof globalThis & Window
		if (!mikMakAppId) return

		// Check for MikMak button blocks (class: swn-tag-wtb-btn)
		const hasMikMakButton = document.querySelector('.swn-tag-wtb-btn')

		// Check for MikMak widget blocks (attribute: data-mm-wtbid)
		const hasMikMakWidget = document.querySelector('[data-mm-wtbid]')

		if (hasMikMakButton || hasMikMakWidget) {
			// If SDK is already loaded, it auto-detects widgets on SPA navigation
			// MikMak SDK observes DOM changes, so we just need to ensure the script is present
			if (win.mikMakLoaded) return
			// Guard against in-flight requests
			const existingScript = document.querySelector(
				'script[src*="wtb-tag.mikmak.ai"]'
			)
			if (existingScript) return

			// Initialize swnDataLayer
			win.swnDataLayer ??= {}
			const dataLayer = win.swnDataLayer
			dataLayer.appId = mikMakAppId
			dataLayer.eventBuffer = dataLayer.eventBuffer || []
			dataLayer.loadBuffer = dataLayer.loadBuffer || []
			dataLayer.push =
				dataLayer.push ||
				((event: unknown) => {
					dataLayer.eventBuffer?.push(event)
				})
			dataLayer.load =
				dataLayer.load ||
				((event: unknown) => {
					dataLayer.loadBuffer?.push(event)
				})
			dataLayer.dnt = dataLayer.dnt ?? true

			// Load MikMak SDK
			const script = document.createElement('script')
			script.async = true
			script.src = `//wtb-tag.mikmak.ai/scripts/${dataLayer.appId}/tag.min.js`

			script.onload = () => {
				win.mikMakLoaded = true
			}
			script.onerror = () => {
				logMessage('Failed to load MikMak SDK')
				// Remove the failed script so the next effect run (e.g. on
				// route change) is not blocked by the existingScript guard.
				script.remove()
				win.mikMakLoaded = false
			}

			const firstScript = document.getElementsByTagName('script')[0]
			if (firstScript?.parentNode) {
				firstScript.parentNode.insertBefore(script, firstScript)
				// Set flag immediately to prevent race condition
				win.mikMakLoaded = true
			}
		}
	}, [mikMakAppId, router.asPath])

	return null
}
